import { useState } from "react";
import {
  InfiniteListBase,
  useListContext,
  useTranslate,
  useRedirect,
} from "ra-core";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MobileListHeader } from "../layout/MobileListHeader";
import { MobileContent } from "../layout/MobileContent";
import { InfinitePagination } from "../misc/InfinitePagination";
import { ContractStatusBadge } from "./ContractStatusBadge";
import { ContractCreateSheet } from "./ContractCreateSheet";
import type { Contract } from "../types";

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  subscription: "Подписка",
  installment: "Рассрочка",
  one_time: "Разовый",
};

export const MobileContractList = () => (
  <InfiniteListBase
    perPage={50}
    sort={{ field: "created_at", order: "DESC" }}
  >
    <MobileContractListLayout />
  </InfiniteListBase>
);

const MobileContractListLayout = () => {
  const translate = useTranslate();
  const { data, isPending, error } = useListContext<Contract>();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <>
      <MobileListHeader
        title={translate("resources.contracts.name", { smart_count: 2 })}
        onCreateClick={() => setCreateOpen(true)}
      />
      <ContractCreateSheet open={createOpen} onOpenChange={setCreateOpen} />
      <MobileContent>
        {isPending ? (
          <MobileContractSkeleton />
        ) : error ? (
          <p className="text-sm text-destructive">
            {translate("ra.message.error")}
          </p>
        ) : data?.length ? (
          <div className="space-y-3">
            {data.map((contract) => (
              <MobileContractCard key={contract.id} contract={contract} />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center space-y-2">
            <p className="text-lg font-semibold">
              {translate("resources.contracts.empty.title")}
            </p>
            <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
              {translate("resources.contracts.action.new", { _: "Новый договор" })}
            </Button>
          </div>
        )}
        <div className="flex justify-center mt-4">
          <InfinitePagination />
        </div>
      </MobileContent>
    </>
  );
};

const MobileContractCard = ({ contract }: { contract: Contract }) => {
  const redirect = useRedirect();

  const typeLabel = PAYMENT_TYPE_LABELS[contract.payment_type] ?? contract.payment_type;
  const amount = contract.payment_type === "subscription"
    ? contract.monthly_amount
    : contract.total_amount;

  return (
    <Card
      className="py-3 cursor-pointer active:opacity-70"
      role="button"
      tabIndex={0}
      onClick={() => redirect(`/contracts/${contract.id}/show`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          redirect(`/contracts/${contract.id}/show`);
        }
      }}
    >
      <CardContent className="px-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <span className="font-medium text-sm leading-snug flex-1">
            {contract.title}
          </span>
          <ContractStatusBadge status={contract.status} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {amount ? (
            <span className="text-base font-semibold">
              {Number(amount).toLocaleString("ru-RU")} ₽
            </span>
          ) : null}
          <Badge variant="outline" className="text-xs">
            {typeLabel}
          </Badge>
        </div>
        {contract.next_payment_date && (
          <p className="text-xs text-muted-foreground">
            Следующий платёж:{" "}
            {new Date(contract.next_payment_date).toLocaleDateString("ru-RU")}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const MobileContractSkeleton = () => (
  <div className="space-y-3">
    {Array.from({ length: 4 }).map((_, i) => (
      <Skeleton key={i} className="h-24 w-full rounded-xl" />
    ))}
  </div>
);
